/**
 * Deploys reaching visitors who already have the app installed.
 *
 * A start (cold launch, new tab, reload) is answered by the service worker's cached shell at
 * once, as before. While the app boots, one tiny request (started in index.html's <head>) asks
 * the network which build is current: /wf-build.txt, whose answer is also a response header
 * (X-WF-Build), so no body has to be read. The first render waits for it, but only until it
 * lands (about one round trip after the page starts, most of it overlapping the boot) and never
 * beyond FRESH_WAIT_MS. If it says a newer build was deployed, the page hides itself (the sky
 * gradient behind it stays), keeps the deployed HTML for the worker (the FRESH cache,
 * public/sw.js) and reloads before the old build has shown a thing: the first visit after a
 * deploy is the new build, with no flash. An answer later than the wait is still acted on if
 * the app has not rendered yet; once a visitor may have seen the page it is never swapped out
 * from under them — it marks the tab for a swap at a quiet moment, like the case below.
 *
 * A tab that stays open across a deploy keeps running the build it loaded: the new worker takes
 * over underneath it and announces which build it holds. This module remembers that, and swaps
 * the tab onto the new build only at a quiet moment, never in the middle of something:
 *   · the tab is hidden (the user switched away; the reload is invisible),
 *   · a new view opens (Home → Map/Studio becomes a real page load),
 *   · another place is picked (the place is saved first; the new page opens on it).
 * At most one swap a minute, so a surprise (a rollout still reaching the edge) can never loop.
 */
const SWAP_KEY = 'wf-update-swap'
const SWAP_GAP_MS = 60_000
/** A tab hidden this long is swapped; shorter absences (app switcher, a permission sheet) are not. */
const HIDDEN_SWAP_MS = 3000
/** Coming back after this long, the tab asks which build the worker holds (it may have slept through the announcement). */
const RESUME_CHECK_MS = 60_000
const UPDATE_CHECK_MS = 10 * 60_000
/** The service worker's cache for a deploy's HTML found by a page (public/sw.js: FRESH). */
const FRESH_CACHE = 'wf-fresh'

const ownBuild = (): string | null => document.querySelector<HTMLMetaElement>('meta[name="wf-build"]')?.content ?? null

let pending = false
let swapping = false
let lastSwap = 0
let hiddenTimer: ReturnType<typeof setTimeout> | undefined

/** A swap has started: this page is going away, and nothing else may reload it (a second reload would cancel the first). */
export const swapInProgress = (): boolean => swapping

/** True when the service worker holds another build than the one this tab runs. */
export const updatePending = (): boolean => pending

/** The worker's build (announced when it takes over, or answered when asked). */
export function noteWorkerBuild(version: unknown): void {
  const own = ownBuild()
  // Development builds and pages without a stamp never swap.
  if (typeof version !== 'string' || !own || own === 'dev') return
  pending = version !== own
  if (pending && document.visibilityState === 'hidden') scheduleHiddenSwap()
}

/** The longest the first render waits for the network's word on the current build. */
export const FRESH_WAIT_MS = 120

let rendered = false

/** The app is about to render: from here on a visitor may see the page, so it is no longer swapped out from under them. */
export function markRendered(): void {
  rendered = true
}

/** The deployed page itself (for the worker to hand to the next navigation); null when it cannot be had. */
async function fetchDeployedPage(): Promise<Response | null> {
  try {
    const res = await fetch('/', { cache: 'no-cache', headers: { accept: 'text/html' } })
    return res.ok && (res.headers.get('content-type') ?? '').includes('text/html') ? res : null
  } catch {
    return null
  }
}

/**
 * The network's word on which build is current: the tiny request index.html started (absent on
 * a first visit, in development, or when no worker answered the page). A newer build marks the
 * tab and leaves its HTML for the worker; seen in time, the page is replaced at once.
 */
export function checkFresh(): Promise<'current' | 'newer' | 'swapping' | 'unknown'> {
  const started = (window as unknown as { __wfFresh?: Promise<Response | null> }).__wfFresh
  const own = ownBuild()
  if (!started || !own || own === 'dev') return Promise.resolve('current')
  return started.then(
    async (res) => {
      if (!res || !res.ok) return 'unknown'
      // The build id is a response header; a host that strips it still has it in the body.
      const theirs = (res.headers.get('x-wf-build') ?? (await res.text())).trim()
      if (!/^[0-9a-f]{12}$/.test(theirs)) return 'unknown'
      if (theirs === own) {
        void res.body?.cancel().catch(() => undefined)
        return 'current'
      }
      pending = true
      // Not rendered yet: hide the page now (the sky gradient behind it stays), so nothing old can show while the new page is fetched and saved.
      const unseen = !rendered && swapAllowed()
      if (unseen) document.documentElement.style.visibility = 'hidden'
      const page = await Promise.race([fetchDeployedPage(), new Promise<null>((resolve) => setTimeout(() => resolve(null), 1500))])
      if (page) await Promise.race([caches.open(FRESH_CACHE).then((cache) => cache.put('/', page)), new Promise((resolve) => setTimeout(resolve, 1000))]).catch(() => undefined)
      if (unseen && page && swapToUpdate()) return 'swapping'
      document.documentElement.style.visibility = ''
      if (document.visibilityState === 'hidden') scheduleHiddenSwap()
      return 'newer'
    },
    () => 'unknown' as const,
  )
}

/**
 * Before the first render: wait (briefly) for the verdict. True when a newer build was found in
 * time and this page is being replaced by it, so it must not render.
 */
export async function replacedByNewerBuild(verdict: ReturnType<typeof checkFresh>): Promise<boolean> {
  const answer = await Promise.race([verdict, new Promise<'late'>((resolve) => setTimeout(() => resolve('late'), FRESH_WAIT_MS))])
  return answer === 'swapping'
}

/** The server no longer has a chunk this tab's build asked for: a newer deploy replaced it. */
export function noteStaleBuild(): void {
  const own = ownBuild()
  if (!own || own === 'dev' || swapping) return
  pending = true
  if (document.visibilityState === 'hidden') scheduleHiddenSwap()
}

/** True when a swap may start now (at most one a minute). */
function swapAllowed(): boolean {
  const now = Date.now()
  try {
    lastSwap = Math.max(lastSwap, Number(sessionStorage.getItem(SWAP_KEY) ?? 0))
  } catch {
    /* private mode: this page's own memory only */
  }
  return now - lastSwap >= SWAP_GAP_MS
}

/** Loads the newer build now, if there is one: this page again, or `url`. True when a load was started. */
export function swapToUpdate(url?: string): boolean {
  if (!pending || swapping || !swapAllowed()) return false
  swapping = true
  lastSwap = Date.now()
  try {
    sessionStorage.setItem(SWAP_KEY, String(lastSwap))
  } catch {
    /* private mode */
  }
  if (url) location.assign(url)
  else location.reload()
  return true
}

function scheduleHiddenSwap(): void {
  clearTimeout(hiddenTimer)
  hiddenTimer = setTimeout(() => {
    if (document.visibilityState === 'hidden') swapToUpdate()
  }, HIDDEN_SWAP_MS)
}

/** Production only: listen to the worker and look for deploys when the tab comes back. */
export function watchForUpdates(): void {
  const sw = navigator.serviceWorker
  if (!sw) return
  const ask = () => sw.controller?.postMessage({ type: 'wf-sw-version?' })
  sw.addEventListener('message', (event: MessageEvent) => {
    if (event.data?.type === 'wf-sw-version') noteWorkerBuild(event.data.version)
  })
  // A new worker took over this tab: ask what it holds (its announcement may have come first).
  sw.addEventListener('controllerchange', ask)
  let hiddenAt = 0
  let checkedAt = Date.now()
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      hiddenAt = Date.now()
      if (pending) scheduleHiddenSwap()
      return
    }
    clearTimeout(hiddenTimer)
    const now = Date.now()
    // Back after a while: the worker looks for a new deploy, and says what it holds.
    if (hiddenAt && now - hiddenAt >= RESUME_CHECK_MS) ask()
    if (now - checkedAt >= UPDATE_CHECK_MS) {
      checkedAt = now
      void sw.getRegistration().then((reg) => reg?.update()).catch(() => undefined)
    }
  })
}
